import { writable } from 'svelte/store';

export interface Theme {
  id: string;
  name: string;
  accent: string;
  colors: {
    'accent': string;
    'accent-hover': string;
    'accent-light': string;
    'accent-glow': string;
    'bg-dark': string;
    'bg-panel': string;
    'bg-card': string;
    'bg-input': string;
    'text-primary': string;
    'text-secondary': string;
    'text-muted': string;
    'border': string;
    'success': string;
    'error': string;
    'warning': string;
    'user-msg': string;
    'assistant-msg': string;
  };
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function makeTheme(id: string, name: string, accent: string, accentHover: string, bgDark: string, bgPanel: string, bgCard: string, textPrimary: string, textSecondary: string, textMuted: string, border: string, success: string, error: string, warning: string): Theme {
  return {
    id, name, accent,
    colors: {
      'accent': accent,
      'accent-hover': accentHover,
      'accent-light': hexToRgba(accent, 0.1),
      'accent-glow': hexToRgba(accent, 0.3),
      'bg-dark': bgDark,
      'bg-panel': bgPanel,
      'bg-card': bgCard,
      'bg-input': bgPanel,
      'text-primary': textPrimary,
      'text-secondary': textSecondary,
      'text-muted': textMuted,
      'border': border,
      'success': success,
      'error': error,
      'warning': warning,
      'user-msg': hexToRgba(accent, 0.15),
      'assistant-msg': bgCard,
    },
  };
}

export const themes: Theme[] = [
  makeTheme('ember', 'Ember', '#EC7211', '#EB5F07', '#0F1419', '#1A2028', '#242D38', '#F1F5F9', '#94A3B8', '#5C6B77', '#334155', '#4ADE80', '#F87171', '#FBBF24'),
  makeTheme('glacier', 'Glacier', '#38BDF8', '#0EA5E9', '#0F172A', '#1E293B', '#334155', '#F1F5F9', '#94A3B8', '#64748B', '#475569', '#4ADE80', '#F87171', '#FBBF24'),
  makeTheme('nebula', 'Nebula', '#A855F7', '#9333EA', '#09090B', '#18181B', '#27272A', '#FAFAFA', '#A1A1AA', '#71717A', '#3F3F46', '#4ADE80', '#F87171', '#FBBF24'),
  makeTheme('evergreen', 'Evergreen', '#34D399', '#10B981', '#0F1F14', '#1A2E1F', '#243B2A', '#ECFDF5', '#6EE7B7', '#4B7A5B', '#2D4A35', '#A3E635', '#F87171', '#FDE047'),
  makeTheme('rosewood', 'Rosewood', '#FB7185', '#F43F5E', '#1C1017', '#2A1B24', '#382631', '#FFF1F2', '#FDA4AF', '#7A5565', '#4A3342', '#4ADE80', '#FCA5A5', '#FBBF24'),
  makeTheme('seafoam', 'Seafoam', '#2DD4BF', '#14B8A6', '#0D1B1E', '#162A2E', '#1F383D', '#F0FDFA', '#99F6E4', '#4D7A7F', '#2A4A50', '#4ADE80', '#F87171', '#FBBF24'),
  makeTheme('aureate', 'Aureate', '#D4A537', '#B8860B', '#141210', '#1F1C18', '#2A2620', '#FAF5E4', '#C8B88A', '#7A7058', '#3D3628', '#4ADE80', '#F87171', '#FDE68A'),
  makeTheme('graphite', 'Graphite', '#A8A8A8', '#888888', '#121212', '#1E1E1E', '#2A2A2A', '#E0E0E0', '#888888', '#666666', '#3A3A3A', '#4ADE80', '#F87171', '#FBBF24'),
];

const STORAGE_KEY = 'debugger-theme';

function getInitialTheme(): Theme {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const found = themes.find(t => t.id === saved);
      if (found) return found;
    }
  }
  return themes[0];
}

export const currentTheme = writable<Theme>(getInitialTheme());

export function setTheme(theme: Theme) {
  currentTheme.set(theme);
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, theme.id);
  }
}

export function applyTheme(theme: Theme, animate = false) {
  const root = document.documentElement;
  if (animate) {
    root.classList.add('theme-transitioning');
  }
  for (const [key, value] of Object.entries(theme.colors)) {
    root.style.setProperty(`--theme-${key}`, value);
  }
  if (animate) {
    setTimeout(() => root.classList.remove('theme-transitioning'), 500);
  }
}
